import {
  APP_KIND,
  STORAGE_PREFIX,
  V2_AUTH_API_BASE_URL,
  V2_AUTH_ME_PATH,
  V2_AUTH_REFRESH_PATH,
  V2_AUTH_TOKEN_PATH,
} from "@config";

const key = (name) => `${STORAGE_PREFIX}${name}`;

function stores() {
  return [window.sessionStorage, window.localStorage];
}

function read(name) {
  for (const storage of stores()) {
    const value = storage.getItem(key(name));
    if (value) return value;
  }
  return null;
}

function write(name, value, remember) {
  const target = remember ? window.localStorage : window.sessionStorage;
  target.setItem(key(name), value);
}

function remove(name) {
  for (const storage of stores()) {
    storage.removeItem(key(name));
  }
}

export function getV2AccessToken() {
  return read("token");
}

export function getV2RefreshToken() {
  return read("refresh_token");
}

export function clearV2Session() {
  ["token", "refresh_token", "user_id", "club_id", "app"].forEach(remove);
}

export function getV2Identity() {
  return {
    user_id: read("user_id"),
    club_id: read("club_id"),
    app: read("app"),
    roles: [],
    account_kind: "club_user",
  };
}

function persistSession(data, remember) {
  const session = {
    token: data?.token || data?.access_token,
    refresh_token: data?.refresh_token,
    user_id: data?.user_id,
    club_id: data?.club_id,
    app: data?.app,
  };

  Object.entries(session).forEach(([name, value]) => {
    if (value) write(name, value, remember);
  });
}

function responseError(data, fallback, status) {
  const message =
    typeof data?.error === "string" ? data.error : data?.error?.message || fallback;
  const error = new Error(message);
  error.code = data?.error?.code;
  error.status = status;
  return error;
}

async function request(path, options = {}) {
  const response = await fetch(`${V2_AUTH_API_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw responseError(data, "Authentication request failed.", response.status);
  }
  return data;
}

export async function loginV2({ login, password, remember = true }) {
  const data = await request(V2_AUTH_TOKEN_PATH, {
    method: "POST",
    body: JSON.stringify({
      login,
      password,
      application: APP_KIND,
    }),
  });

  clearV2Session();
  persistSession(data, remember);

  return data;
}

export async function refreshV2() {
  const refreshToken = getV2RefreshToken();
  if (!refreshToken) return null;

  const data = await request(V2_AUTH_REFRESH_PATH, {
    method: "POST",
    headers: { Authorization: `Bearer ${refreshToken}` },
  });

  const remember = Boolean(window.localStorage.getItem(key("refresh_token")));
  persistSession(data, remember);
  return data;
}

export async function getV2Me() {
  const token = getV2AccessToken();
  if (!token) return null;
  return request(V2_AUTH_ME_PATH, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

